import { Button, Card, CardActions, CardContent, CardHeader, Container, Box, Typography, Paper, Grid, Alert } from "@mui/material"
import { MainLoggedInLayout } from "../../components/layouts/main.loggedIn.layout"
import { useNavigate, useParams } from "react-router"
import { QueryClient, useMutation, useQuery } from "@tanstack/react-query"
import { useContext, useEffect } from "react"
import { UserContext } from "../../context/UserContext"
import { deleteForum, getForum } from "../../controllers/forum/forum"
import { deleteSinglePost } from "../../controllers/posts/posts"
import { Warning, ArrowBack, Delete } from "@mui/icons-material"

export const DeleteForum = () => {
    const { id } = useParams()
    let navigate = useNavigate()    
    const { userDetails } = useContext(UserContext)
    let token = userDetails?.token
    const { data:forum, refetch } = useQuery({ queryKey: ['getSingleForum'], queryFn: () => getForum(token, id) })

    let single_forum = forum?.forum ?? {}
    useEffect(() => {
        refetch()
    })
    
    const queryClient = new QueryClient()
    const mutation = useMutation({
        mutationFn: deleteForum,
        onSuccess: () => {
        // Invalidate and refetch
        queryClient.invalidateQueries({ queryKey: ['deleteForum'] })
        navigate('/forum/list')
        },
    })
    const mutationMastodonDelete = useMutation({
        mutationFn: deleteSinglePost,
        onSuccess: () => {
        // Invalidate and refetch
        queryClient.invalidateQueries({ queryKey: ['deleteSinglePost'] })
        },
    })

    const deleteFrm = () => {
        // delete mastodon status item
        mutationMastodonDelete.mutate({
            id:single_forum?.status,
            token,
        })

        // delete mongo forum item
        mutation.mutate({
          id,
          token,
          status_id: single_forum?.status
        })
    }
    return (
        <MainLoggedInLayout>
            {single_forum?.author?.includes(userDetails?.id) ? (
                <Container maxWidth="md" sx={{ py: 4 }}>
                    <Card sx={{ 
                        borderRadius: 3,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                        border: '2px solid',
                        borderColor: 'error.light'
                    }}>
                        <CardHeader 
                            avatar={
                                <Warning sx={{ color: 'error.main', fontSize: 40 }} />
                            }
                            title={
                                <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                    Delete Forum Topic
                                </Typography>
                            }
                            subheader={
                                <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>
                                    This action cannot be undone
                                </Typography>
                            }
                            sx={{ pb: 1 }}
                        />
                        
                        <CardContent sx={{ p: 4 }}>
                            <Alert severity="error" sx={{ mb: 3 }}>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                    Warning: This is a permanent action
                                </Typography>
                            </Alert>

                            <Paper sx={{ p: 3, backgroundColor: '#fff3e0', border: '1px solid #ffcc02' }}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                    You are about to delete:
                                </Typography>
                                
                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                        Topic: {single_forum?.title || 'Forum Topic'}
                                    </Typography>
                                    {single_forum?.category && (
                                        <Typography variant="body2" color="text.secondary">
                                            Category: {single_forum.category}
                                        </Typography>
                                    )}
                                </Box>

                                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                                    This will permanently delete:
                                </Typography>
                                
                                <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                                    <Typography component="li" variant="body2" color="text.secondary">
                                        The forum topic and all its content
                                    </Typography>
                                    <Typography component="li" variant="body2" color="text.secondary">
                                        All replies and discussions
                                    </Typography>
                                    <Typography component="li" variant="body2" color="text.secondary">
                                        Associated media and attachments
                                    </Typography>
                                    <Typography component="li" variant="body2" color="text.secondary">
                                        All related social media posts
                                    </Typography>
                                </Box>
                            </Paper>

                            <Alert severity="warning" sx={{ mt: 3 }}>
                                <Typography variant="body2">
                                    <strong>Important:</strong> Once deleted, this topic and all associated content cannot be recovered. 
                                    Please make sure you want to proceed with this action.
                                </Typography>
                            </Alert>
                        </CardContent>
                        
                        <CardActions sx={{ p: 3, backgroundColor: '#fafafa' }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Button 
                                        fullWidth 
                                        variant="outlined" 
                                        startIcon={<ArrowBack />}
                                        onClick={() => navigate("/forum/show/"+single_forum?._id)}
                                        sx={{ 
                                            py: 1.5,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        Cancel & Go Back
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Button 
                                        fullWidth 
                                        variant="contained" 
                                        color="error"
                                        startIcon={<Delete />}
                                        onClick={deleteFrm}
                                        sx={{ 
                                            py: 1.5,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 'bold',
                                            '&:hover': {
                                                backgroundColor: 'error.dark'
                                            }
                                        }}
                                    >
                                        Yes, Delete Forever
                                    </Button>
                                </Grid>
                            </Grid>
                        </CardActions>
                    </Card>
                </Container>
            ) : (
                <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
                    <Paper sx={{ p: 4, borderRadius: 3 }}>
                        <Typography variant="h5" color="error" gutterBottom>
                            Access Denied
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            You don't have permission to delete this forum topic. Only the topic author can delete their posts.
                        </Typography>
                        <Button 
                            variant="contained" 
                            onClick={() => navigate("/forum/list")}
                            sx={{ 
                                textTransform: 'none',
                                fontWeight: 'bold',
                                borderRadius: 2
                            }}
                        >
                            Browse Forum Topics
                        </Button>
                    </Paper>
                </Container>
            )}
        </MainLoggedInLayout>
    )
}